/**
 * «Ramadan» (owner 2026-10-31, spec 09): under «Bønnetider», visible all year. The mosque's own Ramadan choices — imsak
 * (sehri ends N minutes before Fajr begins), taraweeh after Isha or at a fixed time, a note each for iftar and sehri —
 * which the app's Ramadan card, the Bønn tab, the screens and the printed calendar all read; «Neste ramadan starter ca.»;
 * and, with the `ramadan_signup` capability, «Påmelding»: «Meld på» in the app, the seats a day, «Opprett lister for
 * ramadan» (one list per Ramadan day, idempotent) and the lists by day with check-in. Results are toasts (useMsg).
 * Rules: core ramadan.js through the server, which checks every field again.
 */
import React, { useEffect, useState } from 'react';
import { can, fieldErrors, errSummary, post, send, type Body } from '../lib/api';
import { switchOnHint } from '../lib/features';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { shortDate } from '../lib/format';
import { Btn, Card, Field, FieldError, LoadError, Loading, Msg, Tag } from '../components/ui';
import { RamadanLists } from './RamadanLists';

type Form = { imsakOffsetMin: string; taraweehMode: string; taraweehAfterMin: string; taraweehFixedTime: string; iftarNote: string; sehriNote: string;
  showIftarSignup: boolean; showSehriSignup: boolean; iftarCapacity: string; sehriCapacity: string };
export const formOf = (s: Body): Form => ({
  imsakOffsetMin: String(s.imsakOffsetMin ?? 0), taraweehMode: s.taraweehMode || 'after_isha', taraweehAfterMin: String(s.taraweehAfterMin ?? 20),
  taraweehFixedTime: s.taraweehFixedTime || '', iftarNote: s.iftarNote || '', sehriNote: s.sehriNote || '',
  showIftarSignup: Boolean(s.showIftarSignup), showSehriSignup: Boolean(s.showSehriSignup),
  iftarCapacity: s.iftarCapacity == null ? '' : String(s.iftarCapacity), sehriCapacity: s.sehriCapacity == null ? '' : String(s.sehriCapacity),
});
/** What the server is sent: numbers as numbers, an empty field as null. */
export const bodyOf = (f: Form) => ({
  imsakOffsetMin: Number(f.imsakOffsetMin) || 0, taraweehMode: f.taraweehMode, taraweehAfterMin: f.taraweehAfterMin === '' ? 20 : Number(f.taraweehAfterMin),
  taraweehFixedTime: f.taraweehFixedTime || null, iftarNote: f.iftarNote || null, sehriNote: f.sehriNote || null,
  showIftarSignup: f.showIftarSignup, showSehriSignup: f.showSehriSignup,
  iftarCapacity: f.iftarCapacity === '' ? null : Number(f.iftarCapacity), sehriCapacity: f.sehriCapacity === '' ? null : Number(f.sehriCapacity),
});
const digits = (v: string) => v.replace(/[^\d]/g, '');

export function Ramadan() {
  const { data, error, reload } = useLoad<Body>('/ramadan');
  const [f, setF] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [listsKey, setListsKey] = useState(0);
  const [msg, say] = useMsg();
  useEffect(() => { if (data) setF(formOf(data.settings)); }, [data]);
  const dirty = Boolean(data && f) && JSON.stringify(bodyOf(f!)) !== JSON.stringify(bodyOf(formOf(data!.settings)));
  useDirty('ramadan', dirty);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data || !f) return <Loading />;

  const write = Boolean(data.can && data.can.write);
  const signup: Body = data.signup || {};
  const lim: Body = data.limits || {};
  const set = (patch: Partial<Form>) => { setF({ ...f, ...patch }); setErrors((e) => { const n = { ...e }; for (const k of Object.keys(patch)) delete n[k]; return n; }); };
  const err = (k: string) => <FieldError text={errors[k]} />;

  const save = async () => {
    setBusy(true);
    const r = await send('/ramadan/settings', { method: 'PUT', body: JSON.stringify(bodyOf(f)) });
    setBusy(false);
    if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
    setErrors({});
    say(r.body.changed ? 'Lagret. Appen, skjermene og utskriftene bruker det nye ved neste oppdatering.' : 'Ingen endringer å lagre.', 'ok');
    await reload();
  };
  const makeLists = async () => {
    say('Oppretter lister …');
    const r = await post('/ramadan/lists', signup.year ? { year: signup.year } : {});
    if (!r.ok) return say((r.body && r.body.message) || 'Det gikk ikke.', 'err');
    const b = r.body;
    const parts = [b.made ? `${b.made} nye lister` : 'ingen nye lister', b.changed ? `${b.changed} med ny kapasitet` : '', b.skipped && b.skipped.length ? `${b.skipped.length} ikke endret (flere påmeldt enn ny kapasitet)` : ''].filter(Boolean);
    say(`Ramadan ${b.hijriYear}: ${parts.join(', ')}.`, b.skipped && b.skipped.length ? 'warn' : 'ok');
    setListsKey((k) => k + 1); await reload();
  };

  const view: Body | null = data.day;
  return (
    <>
      <Card title="Ramadan" hint="Ramadan-modus slår seg på av seg selv etter moskeens Hijri-kalender. Her bestemmer dere imsak, taraweeh og påmelding.">
        <div className="card-body">
          {view ? (
            <p><Tag tone="green">Ramadan {view.hijriDay}</Tag>{' '}Sehri slutter {view.sehriEndsLabel} · Iftar {view.iftarLabel}{view.taraweeh ? ` · Taraweeh ${view.taraweeh.time}` : ''}</p>
          ) : data.nextStart ? (
            <p>Neste ramadan starter ca. <b>{shortDate(data.nextStart.date)}</b> (om {data.nextStart.inDays} dager). Månesikting kan flytte den én dag; juster Hijri-datoen under «Bønnetider».</p>
          ) : null}
        </div>
      </Card>

      <Card title="Tider og notater" hint="Det appens ramadankort, Bønn-fanen, skjermene og ramadankalenderen viser.">
        <div className="card-body">
          <div className="row">
            <Field label="Imsak (minutter før fajr)" className="field half" note={`Sehri slutter så mange minutter før fajr begynner. 0–${lim.imsakMax ?? 30}; 0 = når fajr begynner.`}>{(id, n) => <>
              <input id={id} aria-describedby={n} inputMode="numeric" disabled={!write} value={f.imsakOffsetMin} aria-invalid={Boolean(errors.imsakOffsetMin) || undefined}
                onChange={(e) => set({ imsakOffsetMin: digits(e.target.value) })} />{err('imsakOffsetMin')}</>}</Field>
            <Field label="Taraweeh" className="field half">{(id) =>
              <select id={id} disabled={!write} value={f.taraweehMode} onChange={(e) => set({ taraweehMode: e.target.value })}>
                <option value="after_isha">Minutter etter isha-jamaat</option>
                <option value="fixed">Fast klokkeslett</option>
              </select>}</Field>
          </div>
          <div className="row">
            {f.taraweehMode === 'fixed' ? (
              <Field label="Klokkeslett for taraweeh" className="field half">{(id) => <>
                <input id={id} type="time" disabled={!write} value={f.taraweehFixedTime} aria-invalid={Boolean(errors.taraweehFixedTime) || undefined}
                  onChange={(e) => set({ taraweehFixedTime: e.target.value })} />{err('taraweehFixedTime')}</>}</Field>
            ) : (
              <Field label="Minutter etter isha" className="field half" note={`0–${lim.taraweehAfterMax ?? 180}.`}>{(id, n) => <>
                <input id={id} aria-describedby={n} inputMode="numeric" disabled={!write} value={f.taraweehAfterMin} aria-invalid={Boolean(errors.taraweehAfterMin) || undefined}
                  onChange={(e) => set({ taraweehAfterMin: digits(e.target.value) })} />{err('taraweehAfterMin')}</>}</Field>
            )}
          </div>
          <Field label="Notat om iftar" note={`Én linje i appen, for eksempel «Iftar serveres i kjelleren». Høyst ${lim.noteMax ?? 200} tegn.`}>{(id, n) => <>
            <input id={id} aria-describedby={n} maxLength={lim.noteMax ?? 200} disabled={!write} value={f.iftarNote} onChange={(e) => set({ iftarNote: e.target.value })} />{err('iftarNote')}</>}</Field>
          <Field label="Notat om sehri">{(id) => <>
            <input id={id} maxLength={lim.noteMax ?? 200} disabled={!write} value={f.sehriNote} onChange={(e) => set({ sehriNote: e.target.value })} />{err('sehriNote')}</>}</Field>

          {signup.available ? (
            <>
              <h4 style={{ marginTop: 16 }}>Påmelding i appen</h4>
              <div className="row" style={{ alignItems: 'flex-end' }}>
                <label className="check work-check"><input type="checkbox" disabled={!write} checked={f.showIftarSignup} onChange={(e) => set({ showIftarSignup: e.target.checked })} /> «Meld på iftar»</label>
                <Field label="Plasser til iftar per dag" className="field half" note="Tomt = ingen grense. Med grense: venteliste.">{(id, n) => <>
                  <input id={id} aria-describedby={n} inputMode="numeric" disabled={!write} value={f.iftarCapacity} onChange={(e) => set({ iftarCapacity: digits(e.target.value) })} />{err('iftarCapacity')}</>}</Field>
              </div>
              <div className="row" style={{ alignItems: 'flex-end' }}>
                <label className="check work-check"><input type="checkbox" disabled={!write} checked={f.showSehriSignup} onChange={(e) => set({ showSehriSignup: e.target.checked })} /> «Meld på sehri»</label>
                <Field label="Plasser til sehri per dag" className="field half">{(id) => <>
                  <input id={id} inputMode="numeric" disabled={!write} value={f.sehriCapacity} onChange={(e) => set({ sehriCapacity: digits(e.target.value) })} />{err('sehriCapacity')}</>}</Field>
              </div>
            </>
          ) : <p className="note">Påmelding til iftar og sehri i appen er ikke slått på for moskeen. {switchOnHint('Påmelding til iftar og sehri i appen', can('capabilities.manage'))}</p>}
          {write
            ? <div className="row"><Btn disabled={!dirty || busy} aria-busy={busy || undefined} onClick={save}>{busy ? 'Lagrer …' : 'Lagre'}</Btn></div>
            : <p className="note">Du kan se innstillingene, men ikke endre dem.</p>}
          {data.settings.updatedAt && <p className="note">Sist endret {shortDate(String(data.settings.updatedAt).slice(0, 10))}{data.settings.updatedBy && data.settings.updatedBy.name ? ` av ${data.settings.updatedBy.name}` : ''}.</p>}
          <Msg text={msg.text} tone={msg.tone} />
        </div>
      </Card>

      {signup.available && (
        <Card title="Påmelding" hint={signup.firstDay ? `Ramadan ${signup.hijriYear ?? ''}: ${shortDate(signup.firstDay)} – ${shortDate(signup.lastDay)} (${signup.days} dager, etter moskeens kalender).` : 'Ingen ramadan funnet for året.'}>
          <div className="card-body">
            {data.can && data.can.manage && (
              <div className="row">
                <Btn disabled={dirty || !(signup.kinds && signup.kinds.length)} onClick={makeLists}>Opprett lister for ramadan</Btn>
                <span className="note">{dirty ? 'Lagre innstillingene først.' : !(signup.kinds && signup.kinds.length) ? 'Slå på «Meld på iftar» eller «Meld på sehri» først.'
                  : 'Én liste per dag. Trygt å trykke flere ganger: dager som har liste beholder den.'}</span>
              </div>
            )}
          </div>
          <RamadanLists year={signup.year} reloadKey={listsKey} />
        </Card>
      )}
    </>
  );
}
